import React, { useState } from 'react';
import { GxZone, EmergencyType, createEmergencyAlert } from '../../api/guestExperience';
import { useOfflineAlertQueue } from '../../hooks/useOfflineAlertQueue';

interface EmergencyAlertButtonProps {
  eventId: string;
  zones: GxZone[];
  selectedZoneId?: string;
}

interface EmergencyOption {
  type: EmergencyType;
  icon: string;
  label: string;
}

const EMERGENCY_OPTIONS: EmergencyOption[] = [
  { type: 'Medical', icon: '🏥', label: 'Medical' },
  { type: 'Security', icon: '🔒', label: 'Security' },
  { type: 'Bottleneck', icon: '🚧', label: 'Bottleneck' },
  { type: 'Fire', icon: '🔥', label: 'Fire' },
  { type: 'Other', icon: '❓', label: 'Other' },
];

const styles: Record<string, React.CSSProperties> = {
  emergencyButton: {
    width: '100%',
    minHeight: '56px',
    backgroundColor: '#d32f2f',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '1.125rem',
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    padding: '0.75rem 1rem',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '1rem',
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '1.5rem',
    width: '100%',
    maxWidth: '360px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  },
  modalTitle: {
    fontSize: '1.125rem',
    fontWeight: 700,
    margin: '0 0 1rem 0',
    textAlign: 'center' as const,
    color: '#d32f2f',
  },
  optionButton: {
    width: '100%',
    minHeight: '48px',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '10px',
    fontSize: '1rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    marginBottom: '0.5rem',
  },
  optionIcon: {
    fontSize: '1.5rem',
  },
  optionLabel: {
    fontWeight: 600,
    color: '#333',
  },
  cancelButton: {
    width: '100%',
    minHeight: '44px',
    backgroundColor: '#f5f5f5',
    border: 'none',
    borderRadius: '10px',
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#666',
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
  zoneList: {
    maxHeight: '300px',
    overflowY: 'auto' as const,
  },
  zoneButton: {
    width: '100%',
    minHeight: '44px',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '10px',
    fontSize: '0.9rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    marginBottom: '0.5rem',
  },
  zoneBadge: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  toast: {
    position: 'fixed',
    bottom: '2rem',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#2e7d32',
    color: '#fff',
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: 600,
    zIndex: 10000,
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
  },
  errorToast: {
    position: 'fixed',
    bottom: '2rem',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#d32f2f',
    color: '#fff',
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: 600,
    zIndex: 10000,
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
  },
  submitting: {
    opacity: 0.7,
    pointerEvents: 'none' as const,
  },
  offlineBanner: {
    backgroundColor: '#ff9800',
    color: '#fff',
    padding: '0.5rem 1rem',
    borderRadius: '8px',
    fontSize: '0.8rem',
    fontWeight: 600,
    textAlign: 'center' as const,
    marginBottom: '0.5rem',
  },
  staleOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '1rem',
  },
  staleModal: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '1.5rem',
    width: '100%',
    maxWidth: '360px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  },
  staleTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    margin: '0 0 0.75rem 0',
    textAlign: 'center' as const,
    color: '#ff9800',
  },
  staleMessage: {
    fontSize: '0.875rem',
    color: '#555',
    textAlign: 'center' as const,
    margin: '0 0 1rem 0',
  },
  staleConfirmButton: {
    width: '100%',
    minHeight: '44px',
    backgroundColor: '#d32f2f',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginBottom: '0.5rem',
  },
  staleDismissButton: {
    width: '100%',
    minHeight: '44px',
    backgroundColor: '#f5f5f5',
    border: 'none',
    borderRadius: '10px',
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#666',
    cursor: 'pointer',
  },
};

type ModalStep = 'closed' | 'type-picker' | 'zone-picker';

export function EmergencyAlertButton({ eventId, zones, selectedZoneId }: EmergencyAlertButtonProps) {
  const [step, setStep] = useState<ModalStep>('closed');
  const [selectedType, setSelectedType] = useState<EmergencyType | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const { queueAlert, pendingAlerts, staleAlert, confirmStaleAlert, dismissStaleAlert } =
    useOfflineAlertQueue(eventId);

  const activeZones = zones.filter((z) => z.active);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleEmergencyTap = () => {
    setStep('type-picker');
    setSelectedType(null);
  };

  const handleTypeSelect = (type: EmergencyType) => {
    setSelectedType(type);
    if (selectedZoneId) {
      submitAlert(type, selectedZoneId);
    } else {
      setStep('zone-picker');
    }
  };

  const handleZoneSelect = (zoneId: string) => {
    if (selectedType) {
      submitAlert(selectedType, zoneId);
    }
  };

  const submitAlert = async (emergencyType: EmergencyType, zoneId: string) => {
    const alertData = {
      zoneId,
      emergencyType,
      message: `${emergencyType} emergency reported`,
    };

    // If offline, queue the alert instead of sending
    if (!navigator.onLine) {
      queueAlert(alertData);
      setStep('closed');
      showToast('📶 Alert queued — will send when online', 'success');
      return;
    }

    setSubmitting(true);
    try {
      await createEmergencyAlert(eventId, alertData);

      try {
        navigator.vibrate(200);
      } catch {
        // vibrate not supported in all browsers
      }

      setStep('closed');
      showToast('🚨 Emergency alert sent', 'success');
    } catch (err: unknown) {
      const error = err as { status?: number; message?: string };
      if (error.status === 429) {
        showToast('Please wait before sending another alert', 'error');
      } else {
        // Network error — queue the alert for retry
        queueAlert(alertData);
        setStep('closed');
        showToast('📶 Alert queued — will send when online', 'success');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep('closed');
    setSelectedType(null);
  };

  return (
    <>
      {/* Pending alerts banner */}
      {pendingAlerts.length > 0 && (
        <div style={styles.offlineBanner}>
          📶 {pendingAlerts.length} alert{pendingAlerts.length > 1 ? 's' : ''} queued — waiting for
          connection
        </div>
      )}

      <button
        style={styles.emergencyButton}
        onClick={handleEmergencyTap}
        aria-label="Report emergency"
      >
        🚨 Emergency Alert
      </button>

      {/* Stale Alert Confirmation Dialog */}
      {staleAlert && (
        <div style={styles.staleOverlay as React.CSSProperties} role="dialog" aria-modal="true">
          <div style={styles.staleModal}>
            <h2 style={styles.staleTitle as React.CSSProperties}>Confirm Queued Alert</h2>
            <p style={styles.staleMessage as React.CSSProperties}>
              This {staleAlert.data.emergencyType} alert was queued over 5 minutes ago. Is it still
              relevant?
            </p>
            <button style={styles.staleConfirmButton} onClick={confirmStaleAlert}>
              Yes, send alert
            </button>
            <button style={styles.staleDismissButton} onClick={dismissStaleAlert}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Type Picker Modal */}
      {step === 'type-picker' && (
        <div style={styles.overlay} onClick={handleClose} role="dialog" aria-modal="true">
          <div
            style={{ ...styles.modal, ...(submitting ? styles.submitting : {}) }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={styles.modalTitle}>Select Emergency Type</h2>
            {EMERGENCY_OPTIONS.map((option) => (
              <button
                key={option.type}
                style={styles.optionButton}
                onClick={() => handleTypeSelect(option.type)}
                disabled={submitting}
              >
                <span style={styles.optionIcon}>{option.icon}</span>
                <span style={styles.optionLabel}>{option.label}</span>
              </button>
            ))}
            <button style={styles.cancelButton} onClick={handleClose}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Zone Picker Modal */}
      {step === 'zone-picker' && (
        <div style={styles.overlay} onClick={handleClose} role="dialog" aria-modal="true">
          <div
            style={{ ...styles.modal, ...(submitting ? styles.submitting : {}) }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={styles.modalTitle}>Select Zone</h2>
            <div style={styles.zoneList}>
              {activeZones.map((zone) => (
                <button
                  key={zone.id}
                  style={styles.zoneButton}
                  onClick={() => handleZoneSelect(zone.id)}
                  disabled={submitting}
                >
                  <span style={{ ...styles.zoneBadge, backgroundColor: zone.color }} />
                  <span style={styles.optionLabel}>{zone.name}</span>
                </button>
              ))}
              {activeZones.length === 0 && (
                <p style={{ textAlign: 'center', color: '#999', fontSize: '0.875rem' }}>
                  No active zones available
                </p>
              )}
            </div>
            <button style={styles.cancelButton} onClick={handleClose}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={toast.type === 'success' ? styles.toast : styles.errorToast}>
          {toast.message}
        </div>
      )}
    </>
  );
}
